import { createStore as createZustandStore, type StoreApi } from 'zustand/vanilla';
import type { CharacteristicSelection, SelectionsMap } from './selections';
import type { ValidationError } from '../validation/validate';

export interface ConfigentoState {
    selections: SelectionsMap;
    /**
     * Tracks Characteristics the user has explicitly interacted with. Used
     * by {@link applyPredefine} so that rule-driven defaults never overwrite
     * a deliberate user pick.
     */
    userTouched: Readonly<Record<number, true>>;
    /**
     * Per-Characteristic validation errors, set by deliberate validation
     * passes (ActionButton click, Wizard "Weiter"). Keys are characteristic
     * ids; missing keys mean "no surfaced error". Cleared automatically on
     * any user input for that field.
     */
    validationErrors: Readonly<Record<number, ReadonlyArray<ValidationError>>>;

    /** Pick exactly one Value for a single-pick Characteristic. */
    selectValue: (characteristicId: number, valueId: number | null) => void;
    /** Add/remove a Value for a multi-pick Characteristic. */
    toggleValue: (characteristicId: number, valueId: number) => void;
    /** Set the free-entry text value of text / area Characteristics. */
    setText: (characteristicId: number, value: string) => void;
    /** Set the per-Value quantity for a multiquantity Characteristic. */
    setQuantity: (characteristicId: number, valueId: number, quantity: number) => void;
    /**
     * Admin "Menge" on a select / radio / checkbox / image list: the quantity
     * of the single pick (`valueId` omitted) or of one checked Value.
     */
    setPickQty: (characteristicId: number, qty: number, valueId?: number) => void;
    /** Replace the uploaded-file list of a fileupload Characteristic.
     *  Pass `[]` to wipe everything. */
    setFiles: (characteristicId: number, files: ReadonlyArray<import('./selections').UploadedFile>) => void;
    /** Set the Altcha PoW payload for a captcha Characteristic. Pass `''` to wipe. */
    setCaptchaPayload: (characteristicId: number, payload: string) => void;
    /** Wipe a Characteristic's selection back to "none". Also clears userTouched. */
    resetSelection: (characteristicId: number) => void;

    /**
     * Drop a fully-formed selection into the store. Used by the public
     * restore API to seed the configurator from a snapshot — marks the
     * Characteristic as userTouched so predefine rules don't overwrite
     * the restored pick on the next render.
     */
    setSelection: (characteristicId: number, selection: CharacteristicSelection) => void;

    /**
     * Apply a rule-driven predefine. Idempotent — applying the same value
     * twice produces no state change so the surrounding useEffect can't loop.
     *
     * Two callers with opposite needs, hence `force`:
     *  - schema `default_value` seeding, which must lose against the user
     *    (default), so a reload-restored or deep-linked pick survives;
     *  - a `predefine_values` rule, which is the admin deciding that this
     *    Characteristic gets this Value in this situation — `force: true`.
     *    `resolvePredefineWrites` has already weighed the current selection
     *    against the rule's mode, so re-checking `userTouched` here would
     *    only re-litigate it with less information.
     *
     * Never sets `userTouched` itself: the first predefine would otherwise
     * lock out every later one.
     */
    applyPredefine: (
        characteristicId: number,
        valueId: number | null,
        opts?: { force?: boolean },
    ) => void;

    /**
     * Replace the surfaced validation errors with the given list (keyed by
     * characteristicId). Passing an empty array clears everything. Called
     * by the ActionButton and Wizard step-validation paths.
     */
    setValidationErrors: (errors: ReadonlyArray<ValidationError>) => void;
}

export type ConfigentoStore = StoreApi<ConfigentoState>;

export function createConfigentoStore(): ConfigentoStore {
    return createZustandStore<ConfigentoState>((set) => ({
        selections: {},
        userTouched: {},
        validationErrors: {},

        selectValue: (characteristicId, valueId) =>
            set((state) => {
                // Changing the pick keeps the chosen quantity (legacy keeps
                // the option's whole quantity across choice changes).
                const current = state.selections[characteristicId];
                const qty = current?.kind === 'single' ? current.qty : undefined;
                return writeUserSelection(
                    state,
                    characteristicId,
                    qty === undefined ? { kind: 'single', valueId } : { kind: 'single', valueId, qty },
                );
            }),

        toggleValue: (characteristicId, valueId) =>
            set((state) => {
                const current = state.selections[characteristicId];
                const ids = current?.kind === 'multi' ? current.valueIds : [];
                const idx = ids.indexOf(valueId);
                const next = idx === -1 ? [...ids, valueId] : ids.filter((v) => v !== valueId);
                const qtys = current?.kind === 'multi' ? current.qtys : undefined;
                return writeUserSelection(state, characteristicId, qtys
                    ? { kind: 'multi', valueIds: next, qtys }
                    : { kind: 'multi', valueIds: next });
            }),

        setPickQty: (characteristicId, qty, valueId) =>
            set((state) => {
                const current = state.selections[characteristicId];
                if (valueId === undefined) {
                    const pick = current?.kind === 'single' ? current.valueId : null;
                    return writeUserSelection(state, characteristicId, { kind: 'single', valueId: pick, qty });
                }
                const ids = current?.kind === 'multi' ? current.valueIds : [];
                const qtys = { ...(current?.kind === 'multi' ? current.qtys : {}), [valueId]: qty };
                return writeUserSelection(state, characteristicId, { kind: 'multi', valueIds: ids, qtys });
            }),

        setText: (characteristicId, value) =>
            set((state) =>
                writeUserSelection(state, characteristicId, { kind: 'text', value }),
            ),

        setQuantity: (characteristicId, valueId, quantity) =>
            set((state) => {
                const current = state.selections[characteristicId];
                const existing =
                    current?.kind === 'quantities' ? current.quantities : {};
                const nextQuantities = { ...existing, [valueId]: quantity };
                if (quantity <= 0) {
                    delete nextQuantities[valueId];
                }
                return writeUserSelection(state, characteristicId, {
                    kind: 'quantities',
                    quantities: nextQuantities,
                });
            }),

        setFiles: (characteristicId, files) =>
            set((state) =>
                writeUserSelection(state, characteristicId, {
                    kind: 'files',
                    files: [...files],
                }),
            ),

        setCaptchaPayload: (characteristicId, payload) =>
            set((state) =>
                writeUserSelection(state, characteristicId, {
                    kind: 'captcha',
                    payload,
                }),
            ),

        setSelection: (characteristicId, selection) =>
            set((state) =>
                writeUserSelection(state, characteristicId, selection),
            ),

        resetSelection: (characteristicId) =>
            set((state) => {
                const { [characteristicId]: _droppedSel, ...selectionsRest } = state.selections;
                const { [characteristicId]: _droppedTouched, ...touchedRest } = state.userTouched;
                const { [characteristicId]: _droppedErr, ...errorsRest } = state.validationErrors;
                return {
                    selections: selectionsRest,
                    userTouched: touchedRest,
                    validationErrors: errorsRest,
                };
            }),

        applyPredefine: (characteristicId, valueId, opts) =>
            set((state) => {
                // User wins: an explicit pick blocks the auto-default. A rule
                // write passes `force` — see the interface doc.
                if (!opts?.force && state.userTouched[characteristicId]) return state;

                // Idempotent: skip writes when the selection already matches.
                const current = state.selections[characteristicId];
                if (
                    current?.kind === 'single' &&
                    current.valueId === valueId
                ) {
                    return state;
                }

                return {
                    selections: {
                        ...state.selections,
                        [characteristicId]: { kind: 'single', valueId },
                    },
                };
            }),

        setValidationErrors: (errors) =>
            set(() => {
                const grouped: Record<number, ValidationError[]> = {};
                for (const e of errors) {
                    (grouped[e.characteristicId] ??= []).push(e);
                }
                return { validationErrors: grouped };
            }),
    }));
}

function writeUserSelection(
    state: ConfigentoState,
    characteristicId: number,
    selection: CharacteristicSelection,
): Partial<ConfigentoState> {
    // Any deliberate user input on a Characteristic clears its surfaced
    // validation error — they're addressing it.
    const { [characteristicId]: _drop, ...errorsRest } = state.validationErrors;
    return {
        selections: { ...state.selections, [characteristicId]: selection },
        userTouched: { ...state.userTouched, [characteristicId]: true },
        validationErrors: errorsRest,
    };
}
